'use client'

import React, { useEffect, useState, useRef, useCallback } from 'react'
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'
import { OffersOffer } from '@/services/dataTypes'
import { apiRandomOfferBanners } from '@/apis/offers'
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks'
import Banner from './Banner'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface Props {
    companyId: string
    mer_slug: string
    slug_type: string
    domain: string
}

const HorizontalBannerSlider = ({ companyId, slug_type, mer_slug, domain }: Props) => {
    const [offers, setOffers] = useState<OffersOffer[]>([])
    const [loading, setLoading] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [pagination, setPagination] = useState<any>(null)
    const [isPlaying, setIsPlaying] = useState(true)
    const latestRequest = useRef(0)
    const emptyAttempts = useRef(0)
    const sliderSectionRef = useRef<HTMLElement | null>(null)
    const [isSliderReady, setIsSliderReady] = useState(false)
    const [currentSlide, setCurrentSlide] = useState(0)
    const autoplayRef = useRef<NodeJS.Timeout | null>(null)

    const offersRef = useRef(offers)
    const hasMoreRef = useRef(hasMore)
    const loadingRef = useRef(loading)
    const paginationRef = useRef(pagination)

    useEffect(() => { offersRef.current = offers }, [offers])
    useEffect(() => { hasMoreRef.current = hasMore }, [hasMore])
    useEffect(() => { loadingRef.current = loading }, [loading])
    useEffect(() => { paginationRef.current = pagination }, [pagination])

    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
        loop: true,
        slides: { perView: 1 },
        mode: 'snap',
        created: () => setIsSliderReady(true),
        slideChanged: (s) => {
            setCurrentSlide(s.track.details.rel)
            if (s.track.details.rel === offersRef.current.length - 1 && hasMoreRef.current && !loadingRef.current && paginationRef.current?.next_page) {
                getOffers(paginationRef.current.next_page)
            }
        },
    })

    const startAutoplay = () => {
        if (!slider.current || !isSliderReady || autoplayRef.current) return
        autoplayRef.current = setInterval(() => {
            slider.current?.moveToIdx((slider.current?.track.details.abs || 0) + 1, true, { duration: 3000, easing: t => t })
        }, 5000)
    }
    const stopAutoplay = () => { if (autoplayRef.current) { clearInterval(autoplayRef.current); autoplayRef.current = null } }

    const toggleAutoplay = () => {
        if (isPlaying) {
            stopAutoplay()
            setIsPlaying(false)
        } else {
            startAutoplay()
            setIsPlaying(true)
        }
    }

    useEffect(() => {
        if (!slider.current || !isSliderReady) return
        startAutoplay()
        const wrapper = sliderSectionRef.current
        wrapper?.addEventListener('mouseenter', stopAutoplay)
        wrapper?.addEventListener('mouseleave', startAutoplay)
        return () => { stopAutoplay(); wrapper?.removeEventListener('mouseenter', stopAutoplay); wrapper?.removeEventListener('mouseleave', startAutoplay) }
    }, [slider, isSliderReady])

    const getOffers = useCallback(async (pageNum: number, reset = false) => {
        setLoading(true)
        const requestId = Date.now()
        latestRequest.current = requestId
        try {
            const bannerOffers = await apiRandomOfferBanners(companyId, pageNum)
            if (latestRequest.current !== requestId) return
            const apiOffers = bannerOffers?.data?.offers || []
            const filtered = filterOfferBanners(apiOffers, 300, 1000, 0, 150)
            const paginationData = bannerOffers?.data?.pagination || {}
            setPagination({ current_page: paginationData.current_page, last_page: paginationData.last_page, next_page: paginationData.next_page })
            if (filtered.length === 0 && paginationData.next_page && emptyAttempts.current < 1) {
                emptyAttempts.current += 1; await getOffers(paginationData.next_page, false); return
            } else { emptyAttempts.current = 0 }
            if (reset) setOffers(filtered)
            else if (filtered.length > 0) setOffers(prev => [...prev, ...filtered])
            setHasMore(paginationData.next_page !== null)
        } finally { setLoading(false) }
    }, [companyId])

    useEffect(() => { setOffers([]); setPagination(null); emptyAttempts.current = 0; getOffers(1, true) }, [companyId])
    useEffect(() => { slider.current?.update() }, [offers])

    if (!offers.length && !loading) return null

    return (
        <section ref={sliderSectionRef} className="relative w-full py-8 px-5 overflow-hidden">
            <div className="absolute inset-0 opacity-50"
                style={{
                    background: `url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cdefs%3E%3Cpattern%20id%3D%22dot%22%20width%3D%2220%22%20height%3D%2220%22%20patternUnits%3D%22userSpaceOnUse%22%3E%3Ccircle%20cx%3D%222%22%20cy%3D%222%22%20r%3D%221%22%20fill%3D%22%238C5826%22%20fill-opacity%3D%220.05%22/%3E%3C/pattern%3E%3C/defs%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20fill%3D%22url(%23dot)%22/%3E%3C/svg%3E')`
                }} />

            <div className="relative max-w-7xl mx-auto">
                {offers.length > 0 ? (
                    <>
                        <div className="relative rounded-2xl overflow-hidden shadow-xl">
                            <div ref={sliderRef} className="keen-slider">
                                {offers.map((item, i) => {
                                    const dim = getBannerDimensions(item)
                                    return (
                                        <div key={i} className="keen-slider__slide" style={{ width: '100%', height: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                                            <Banner data={item} height={dim?.height} width={dim?.width} offerLink={null} mer_slug={mer_slug} slug_type={slug_type} domain={domain} />
                                        </div>
                                    )
                                })}
                            </div>

                            {/* Navigation Buttons */}
                            <button
                                onClick={() => slider.current?.prev()}
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 z-10 backdrop-blur-sm"
                                style={{ color: '#8C5826' }}
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <button
                                onClick={() => slider.current?.next()}
                                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 z-10 backdrop-blur-sm"
                                style={{ color: '#8C5826' }}
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="h-48 flex items-center justify-center bg-white/50 rounded-2xl backdrop-blur-sm">
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
                                style={{ borderColor: '#8C5826', borderTopColor: 'transparent' }} />
                            <p className="text-sm" style={{ color: '#9ca3af' }}>Loading amazing deals...</p>
                        </div>
                    </div>
                )}
            </div>
        </section>
    )
}

export default HorizontalBannerSlider